import { Cell, Pie, PieChart } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";

export interface CategorySlice {
  name: string;
  value: number;
}

/** Fixed categorical order — never cycled/reassigned when the filtered set changes. */
const CATEGORY_COLOR_VARS = [
  "var(--color-chart-1)",
  "var(--color-chart-2)",
  "var(--color-chart-3)",
  "var(--color-chart-4)",
  "var(--color-chart-5)",
];

/**
 * Category breakdown donut — used for training-status distribution and
 * pass/fail ratio. Categories beyond the 5 defined chart slots collapse
 * into an "Other" bucket rather than generating a new hue.
 */
export function CategoryPieChart({
  data,
  valueFormatter,
  className,
}: {
  data: CategorySlice[];
  valueFormatter?: (value: number) => string;
  className?: string;
}) {
  const MAX_SLICES = CATEGORY_COLOR_VARS.length;
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const visible = sorted.slice(0, MAX_SLICES);
  const rest = sorted.slice(MAX_SLICES);
  const chartData =
    rest.length > 0
      ? [...visible, { name: "Other", value: rest.reduce((sum, s) => sum + s.value, 0) }]
      : visible;

  const config = chartData.reduce((acc, slice, index) => {
    acc[slice.name] = {
      label: slice.name,
      color: CATEGORY_COLOR_VARS[index % CATEGORY_COLOR_VARS.length],
    };
    return acc;
  }, {} as ChartConfig);

  return (
    <ChartContainer config={config} className={className ?? "aspect-square max-h-72 w-full"}>
      <PieChart>
        <ChartTooltip
          content={
            <ChartTooltipContent
              hideLabel
              nameKey="name"
              formatter={
                valueFormatter
                  ? (value, name) => [valueFormatter(Number(value)), String(name)]
                  : undefined
              }
            />
          }
        />
        <Pie
          data={chartData}
          dataKey="value"
          nameKey="name"
          innerRadius={56}
          outerRadius={90}
          strokeWidth={2}
        >
          {chartData.map((slice, index) => (
            <Cell
              key={slice.name}
              fill={CATEGORY_COLOR_VARS[index % CATEGORY_COLOR_VARS.length]}
              stroke="var(--background)"
            />
          ))}
        </Pie>
        <ChartLegend
          content={({ payload, verticalAlign }) => (
            <ChartLegendContent payload={payload} verticalAlign={verticalAlign} nameKey="name" />
          )}
        />
      </PieChart>
    </ChartContainer>
  );
}
